import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { View } from "ui/atoms/view";
import { Button, ButtonText } from "ui/gluestack/button";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "app/services/store";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { usePasswordVerification } from "../hooks/usePasswordVerification";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useEffect } from "react";

interface Props {
  hide?: boolean;
  title?: string;
  description?: string;
  noBackgroundClose?: boolean;
  showSecondaryButton?: boolean;
  editableEmail?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  blurBackground?: boolean;
  onSuccess: (data: {
    email: string;
    password: string;
  }) => Promise<void> | void;
  onCancel: () => Promise<void> | void;
}
const ActionSignatureModal = ({
  onCancel,
  onSuccess,
  title,
  hide,
  description,
  noBackgroundClose,
  showSecondaryButton,
  editableEmail,
  primaryButtonText = "Confirm Password",
  secondaryButtonText = "Cancel",
  blurBackground,
}: Props) => {
  const email = useSelector(
    (state: RootState) => state.authorisation?.user?.email ?? "",
  );
  const { verifyPassword, retriesLeft } = usePasswordVerification();

  const { showErrorToast } = useDisplayToast();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      email: editableEmail ? "" : email,
      password: "",
    },
  });

  useEffect(() => {
    if (!hide) {
      reset({
        email: editableEmail ? "" : email,
        password: "",
      });
    }
  }, [hide]);
  const handleClose = async () => {
    const resp = onCancel();
    if (resp && resp instanceof Promise) {
      await resp;
    }
  };

  const submit = handleSubmit(async (data) => {
    try {
      await verifyPassword({ email: data.email, password: data.password });
      const resp = onSuccess(data);
      if (resp && resp instanceof Promise) {
        await resp;
      }
    } catch (e) {
      showErrorToast(e);
    }
  });

  return (
    <ReusableModal
      isVisible={!hide}
      onClose={handleClose}
      className="w-full max-w-[378px] mx-auto"
      title={title || "Confirm action"}
      description={
        description || `We need your password in order to confirm this action.`
      }
      hasCancelButton={showSecondaryButton}
      noBackgroundClose={noBackgroundClose}
      blur={blurBackground}
    >
      <View className="flex gap-4 ">
        <FormControlledInput
          id="sign-email"
          control={control}
          name="email"
          label={editableEmail ? "Email" : "Current email"}
          placeholder={editableEmail ? "Enter email" : "Enter your email"}
          isReadOnly={!editableEmail}
          isRequired
        ></FormControlledInput>
        <FormControlledInput
          id="sign-password"
          control={control}
          name="password"
          type="password"
          label={editableEmail ? "Password" : "Current password"}
          placeholder={editableEmail ? "Enter password" : "Enter your password"}
          isRequired
          autoFocus
          helperText={
            retriesLeft
              ? `Only ${retriesLeft} ${retriesLeft === 1 ? "retry" : "retires"} left`
              : ""
          }
          onSubmitEditing={submit}
        ></FormControlledInput>
        <Button
          action="primary"
          size="sm"
          variant="solid"
          onPress={submit}
          disabled={isSubmitting}
        >
          <ButtonText>{primaryButtonText}</ButtonText>
        </Button>
        {showSecondaryButton ? (
          <Button
            action="secondary"
            size="sm"
            variant="outline"
            onPress={handleClose}
            disabled={isSubmitting}
          >
            <ButtonText>{secondaryButtonText}</ButtonText>
          </Button>
        ) : null}
      </View>
    </ReusableModal>
  );
};

ActionSignatureModal.displayName = "ActionSignatureModal";
export { ActionSignatureModal };
